import api from '../config/api';
import logger from '../utils/logger';

// Normalize backend brand payloads (camelCase <-> snake_case, nested, etc.)
const normalizeBrand = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  // If wrapped
  if (raw.brand && typeof raw.brand === 'object') {
    return normalizeBrand(raw.brand);
  }

  // If nested under settings
  if (raw.settings && raw.settings.brand) {
    return normalizeBrand(raw.settings.brand);
  }

  // Direct fields (accept both camelCase and snake_case)
  const name = raw.name || raw.brandName || raw["brand_name"] || 'CoffeeCloud';
  const logoUrl = raw.logoUrl || raw["logo_url"] || raw.logo || '/logo.png';
  const faviconUrl = raw.faviconUrl || raw["favicon_url"] || raw.favicon || '/favicon.ico';

  return { name, logoUrl, faviconUrl };
};

// In-memory cache to reduce flicker and network calls
let inMemoryBrandCache = { data: null, ts: 0 };
const BRAND_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Append a version query param to ensure users see updated assets without hard refresh
const appendVersion = (url, version) => {
  try {
    if (!url || typeof url !== 'string') return url;
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    if (!version) return u.toString();
    u.searchParams.set('v', String(version));
    return u.toString();
  } catch {
    return url;
  }
};

class BrandService {
  async getBrandSettings(skipCache = false) {
    try {
      // Serve from memory cache if fresh and not explicitly skipping cache
      if (!skipCache && inMemoryBrandCache.data && Date.now() - inMemoryBrandCache.ts < BRAND_CACHE_TTL_MS) {
        return inMemoryBrandCache.data;
      }

      // Prefer clean URL for cached fetch; add cache-bust only when skipCache=true
      const url = skipCache ? `public/brand?_=${Date.now()}` : 'public/brand';
      const response = await api.get(url, {
        headers: skipCache
          ? {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          : {},
        timeout: 8000
      });

      const data = response?.data ?? {};
      // Accept multiple shapes
      const normalized = normalizeBrand(data) || normalizeBrand(data.brand);
      if (normalized) {
        // Use local update timestamp to version-bust asset URLs
        const versionFromLocal = (() => {
          try { return localStorage.getItem('brandSettingsUpdated'); } catch { return null; }
        })();
        const safe = {
          name: typeof normalized.name === 'string' && normalized.name.trim() ? normalized.name.trim() : 'CoffeeCloud',
          logoUrl: appendVersion(normalized.logoUrl, versionFromLocal),
          faviconUrl: appendVersion(normalized.faviconUrl, versionFromLocal)
        };
        inMemoryBrandCache = { data: safe, ts: Date.now() };
        return safe;
      }

      throw new Error('Invalid brand response shape');
    } catch (error) {
  logger.error('Brand service getBrandSettings error:', error?.message || error);
      // Fallback defaults to keep UI functional
      const fallback = {
        name: 'CoffeeCloud',
        logoUrl: '/logo.png',
        faviconUrl: '/favicon.ico'
      };
      inMemoryBrandCache = { data: fallback, ts: Date.now() };
      return fallback;
    }
  }

  async updateBrandSettings(brandSettings, options = { verify: true }) {
    // Try primary endpoint with key-value settings map first
    const payloadSettingsMap = {
      'brand.name': brandSettings.name,
      'brand.logo_url': brandSettings.logoUrl,
      'brand.favicon_url': brandSettings.faviconUrl,
    };
    const noCacheHeaders = {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      timeout: 8000
    };

    const markUpdated = () => {
      try { localStorage.setItem('brandSettingsUpdated', Date.now().toString()); } catch {}
      // Invalidate in-memory cache so next read fetches fresh
      inMemoryBrandCache = { data: null, ts: 0 };
    };

    try {
  const resp = await api.post('admin/settings', payloadSettingsMap, noCacheHeaders);
      if (resp?.status >= 200 && resp?.status < 300 && resp?.data?.success !== false) {
        markUpdated();
        if (options?.verify) {
          const fresh = await this.getBrandSettings(true);
          const sentName = (brandSettings.name || '').trim();
          if (sentName && fresh?.name !== sentName) {
            return { success: false, error: 'Verification failed: name mismatch' };
          }
        }
        return { success: true };
      }
      // If backend explicitly says fail, fall through to alt
      throw new Error(resp?.data?.message || 'Primary settings endpoint returned failure');
    } catch (primaryErr) {
  logger.warn('Primary brand update failed, retrying alternate format:', primaryErr?.message || primaryErr);
      // Try alternate endpoint/format commonly used
      const altPayload = {
        name: brandSettings.name,
        logoUrl: brandSettings.logoUrl,
        logo_url: brandSettings.logoUrl,
        faviconUrl: brandSettings.faviconUrl,
        favicon_url: brandSettings.faviconUrl,
      };
      try {
  const altResp = await api.post('admin/brand', altPayload, noCacheHeaders);
        if (altResp?.status >= 200 && altResp?.status < 300 && altResp?.data?.success !== false) {
          markUpdated();
          if (options?.verify) {
            const fresh = await this.getBrandSettings(true);
            const sentName = (brandSettings.name || '').trim();
            if (sentName && fresh?.name !== sentName) {
              return { success: false, error: 'Verification failed: name mismatch' };
            }
          }
          return { success: true };
        }
        throw new Error(altResp?.data?.message || 'Alternate brand endpoint returned failure');
      } catch (altErr) {
  logger.error('Both brand update attempts failed:', altErr?.message || altErr);
        return { success: false, error: altErr?.message || 'Failed to update brand settings' };
      }
    }
  }

  async getAdminSettings() {
    try {
      const timestamp = Date.now();
  const response = await api.get(`admin/settings?_=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        timeout: 8000
      });
      return response?.data;
    } catch (error) {
  logger.error('Failed to fetch admin settings:', error?.message || error);
      throw new Error('Failed to fetch admin settings');
    }
  }
}

const brandService = new BrandService();
export default brandService;
